"use client";

import { serializeStateHybrid } from "@/app/directories/kiosk/machine/state-serializer";
import { Checkbox } from "@frontend/shared/components/ui/checkbox";
import { Copy, FileDown } from "lucide-react";
import { useEffect, useState } from "react";
import { traceInspector } from "../../trace-inspector/trace-inspector";
import type { StateSize } from "../../types";
import { ContextTable } from "../ContextTable";
import { ContextTree } from "../ContextTree";

interface InspectTabProps {
  state: {
    value: string | object;
    context: Record<string, unknown>;
  };
  sizeInfo: StateSize;
  onCopy: () => void;
  copied: boolean;
  onContextChange?: (updates: Record<string, unknown>) => void;
  onStateChange?: (
    stateName: string,
    arc?: { arcId: string; stateName: string | Record<string, unknown> },
  ) => void;
}

export function InspectTab({
  state,
  sizeInfo,
  onCopy,
  copied,
  onContextChange,
  onStateChange,
}: InspectTabProps) {
  const [subTab, setSubTab] = useState<"state" | "size">("state");
  const [viewMode, setViewMode] = useState<"json" | "tree" | "table">("tree");
  const [jsonCopied, setJsonCopied] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [jumpToState, setJumpToState] = useState(true);

  // Subscribe to arc child context for jump-to-state copy/paste support
  const [childSnapshot, setChildSnapshot] = useState(() =>
    traceInspector.getChildSnapshot(),
  );
  useEffect(() => {
    return traceInspector.subscribeToChildContext(() => {
      setChildSnapshot(traceInspector.getChildSnapshot());
    });
  }, []);

  // Get current state name
  const currentStateName = (() => {
    if (typeof state.value === "string") {
      return state.value;
    }

    if (typeof state.value === "object" && state.value !== null) {
      // Nested state: { posPayment: "creatingPaymentIntent" }
      const [parentName, childName] = Object.entries(state.value)[0] ?? [];
      return parentName; // Use parent name we already extracted
    }

    return String(state.value);
  })();

  const handleCopyJson = async () => {
    try {
      // Read directly from the inspector at copy time — React state may lag
      // if the arc transitioned without a context change (e.g., verify screen).
      const latestChildSnapshot = traceInspector.getChildSnapshot();
      const fullState = {
        value: state.value,
        context: state.context,
        // Include active arc snapshot so paste can restore arc-internal state
        ...(latestChildSnapshot && { arc: latestChildSnapshot }),
      };
      const json = JSON.stringify(fullState, null, 2);
      await navigator.clipboard.writeText(json);
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy JSON:", error);
    }
  };

  const handlePasteJson = () => {
    try {
      const parsed = JSON.parse(pasteText);

      // Validate structure
      if (!parsed || typeof parsed !== "object") {
        alert("Invalid state: must be a JSON object");
        return;
      }

      // Check if it's a full state (with value + context) or just context
      if (parsed.value !== undefined && parsed.context !== undefined) {
        // Full state paste
        if (typeof parsed.context !== "object") {
          alert("Invalid state: 'context' must be an object");
          return;
        }

        // If jump to state is enabled, combine state + context update in single navigation
        if (jumpToState) {
          if (
            parsed.arc &&
            typeof parsed.arc === "object" &&
            parsed.arc !== null
          ) {
            // Arc jump: include the arc field in the serialized state so it
            // travels through the stateId URL param (same as non-arc jumps).
            // This avoids the localStorage-only path which is unavailable
            // during SSR, causing the snapshot to always be null on load.
            const arc = parsed.arc as {
              arcId: string;
              stateName: string | Record<string, unknown>;
              context: Record<string, unknown>;
            };
            const serialized = {
              value: parsed.value as string,
              context: parsed.context as Record<string, unknown>,
              arc,
            };

            const url = new URL(window.location.href);
            const currentStateId = url.searchParams.get("stateId");

            const { encoded, method } = serializeStateHybrid(
              serialized,
              currentStateId || undefined,
            );

            const paramName = method === "storage" ? "stateId" : "state";
            url.searchParams.set(paramName, encoded);
            url.searchParams.delete(method === "storage" ? "state" : "stateId");
            window.location.href = url.toString();
          } else {
            // No arc field: use regular URL serialization for both arc-invoking
            // and non-arc states. For arc-invoking states, machine.resolveState()
            // in the provider creates a live snapshot and the arc starts fresh —
            // XState fires always transitions normally on fresh-start actors.
            // (Restoring a persisted snapshot with arc at "route" does NOT
            // re-evaluate always transitions, so we cannot use that path here.)
            const serialized = {
              value: parsed.value,
              context: parsed.context as Record<string, unknown>,
            };

            const url = new URL(window.location.href);
            const currentStateId = url.searchParams.get("stateId");

            const { encoded, method } = serializeStateHybrid(
              serialized,
              currentStateId || undefined,
            );

            const paramName = method === "storage" ? "stateId" : "state";
            url.searchParams.set(paramName, encoded);
            url.searchParams.delete(method === "storage" ? "state" : "stateId");

            window.location.href = url.toString();
          }
        } else {
          // Just update context without changing state
          onContextChange?.(parsed.context);
        }
      } else {
        // Just context paste (backward compatibility)
        onContextChange?.(parsed);
      }

      setPasteMode(false);
      setPasteText("");
    } catch (error) {
      alert(
        `Invalid JSON: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  return (
    <div className="space-y-1 h-full">
      {/* Sub-tabs */}
      <div className="flex gap-2  border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setSubTab("state")}
          className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
            subTab === "state"
              ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          State + Context
        </button>
        <button
          type="button"
          onClick={() => setSubTab("size")}
          className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
            subTab === "size"
              ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          Size Info
        </button>
      </div>

      {/* Sub-tab Content */}
      <div className="h-full">
        {subTab === "state" && (
          <div className="h-full flex flex-col">
            {/* Header with Current State and View Mode Controls */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  State:{" "}
                  <code className="text-blue-900 font-mono">
                    {currentStateName}
                  </code>
                </h3>
                <span className="text-xs text-gray-500">
                  ({Object.keys(state.context).length} fields)
                </span>
              </div>
              <div className="flex gap-2 items-center">
                {/* Copy/Paste buttons for JSON view */}
                {viewMode === "json" && onContextChange && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={handleCopyJson}
                      className="px-3 py-1 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-1"
                      title="Copy full state (value + context)"
                    >
                      <Copy className="h-3 w-3" />
                      {jsonCopied ? "Copied!" : "Copy"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasteMode(!pasteMode);
                        setPasteText("");
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                        pasteMode
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                      title="Paste state JSON"
                    >
                      <FileDown className="h-3 w-3" />
                      {pasteMode ? "Cancel" : "Paste"}
                    </button>
                  </div>
                )}
                {/* View Mode Toggle */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("tree")}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      viewMode === "tree"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Tree
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("table")}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      viewMode === "table"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Table
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("json")}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      viewMode === "json"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    JSON
                  </button>
                </div>
              </div>
            </div>

            {/* Paste Mode UI */}
            {pasteMode && viewMode === "json" && (
              <div className="mb-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Paste state JSON (full state or just context):
                </div>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder='{"value": "welcome", "context": {...}} or just {...}'
                  className="w-full h-32 px-3 py-2 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <div className="mt-2 flex gap-2 items-center justify-end">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                      id="jump-to-state-checkbox"
                      checked={jumpToState}
                      onCheckedChange={(checked) =>
                        setJumpToState(checked === true)
                      }
                    />
                    <label
                      htmlFor="jump-to-state-checkbox"
                      className="cursor-pointer"
                    >
                      Jump to state
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPasteMode(false);
                      setPasteText("");
                    }}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePasteJson}
                    className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                  >
                    Apply JSON
                  </button>
                </div>
              </div>
            )}

            {/* Content Area */}
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-y-auto">
              {viewMode === "json" ? (
                <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(
                    {
                      value: state.value,
                      context: state.context,
                      ...(childSnapshot && {
                        arc: childSnapshot,
                      }),
                    },
                    null,
                    2,
                  )}
                </pre>
              ) : viewMode === "table" ? (
                <div className="space-y-4">
                  {/* State Value Section */}
                  <div className="pb-3 border-b border-gray-300">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">
                        State Value
                      </h4>
                      <button
                        type="button"
                        onClick={async () => {
                          await navigator.clipboard.writeText(
                            typeof state.value === "string"
                              ? state.value
                              : JSON.stringify(state.value),
                          );
                          setJsonCopied(true);
                          setTimeout(() => setJsonCopied(false), 1000);
                        }}
                        className="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        Copy
                      </button>
                    </div>
                    <code className="text-xs font-mono text-blue-900">
                      {typeof state.value === "string"
                        ? state.value
                        : JSON.stringify(state.value, null, 2)}
                    </code>
                  </div>
                  {/* Context Table */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                      Context
                    </h4>
                    <ContextTable
                      data={state.context}
                      onEdit={
                        onContextChange
                          ? (key, value) => {
                              onContextChange({ [key]: value });
                            }
                          : undefined
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* State Value Section */}
                  <div className="pb-3 border-b border-gray-300">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">
                        State Value
                      </h4>
                      <button
                        type="button"
                        onClick={async () => {
                          await navigator.clipboard.writeText(
                            typeof state.value === "string"
                              ? state.value
                              : JSON.stringify(state.value),
                          );
                          setJsonCopied(true);
                          setTimeout(() => setJsonCopied(false), 1000);
                        }}
                        className="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        Copy
                      </button>
                    </div>
                    <code className="text-xs font-mono text-blue-900">
                      {typeof state.value === "string"
                        ? state.value
                        : JSON.stringify(state.value, null, 2)}
                    </code>
                  </div>
                  {/* Context Tree */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
                      Context
                    </h4>
                    <ContextTree data={state.context} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {subTab === "size" && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              State URL Size
            </h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">URL Length:</span>
                <span className="font-mono text-gray-900">
                  {sizeInfo.urlLength} chars
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Encoded State:</span>
                <span className="font-mono text-gray-900">
                  {sizeInfo.encoded} chars
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Uncompressed JSON:</span>
                <span className="font-mono text-gray-900">
                  {sizeInfo.uncompressed} bytes
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Encoding Ratio:</span>
                <span className="font-mono text-gray-900">
                  {sizeInfo.encodingRatio}%
                </span>
              </div>
              <div className="pt-2 border-t border-gray-300 space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <span
                    className={
                      sizeInfo.isWithinRecommendedLimit
                        ? "text-green-600"
                        : "text-orange-600"
                    }
                  >
                    {sizeInfo.isWithinRecommendedLimit ? "✓" : "⚠"}
                  </span>
                  <span className="text-gray-600">
                    Recommended Limit (2KB):{" "}
                    {sizeInfo.isWithinRecommendedLimit ? "Pass" : "Exceeded"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span
                    className={
                      sizeInfo.isWithinServerLimit
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {sizeInfo.isWithinServerLimit ? "✓" : "✗"}
                  </span>
                  <span className="text-gray-600">
                    Server Limit (8KB):{" "}
                    {sizeInfo.isWithinServerLimit ? "Pass" : "Exceeded"}
                  </span>
                </div>
              </div>
            </div>

            {/* Copy Button */}
            <button
              type="button"
              onClick={onCopy}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Copy className="h-4 w-4" />
              {copied ? "✓ Copied to Clipboard!" : "Copy State URL"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
